import { type FC, useState } from 'react';

import { VscMerge } from 'react-icons/vsc';
import { toast } from 'sonner';

import { Button } from '@/components/shadcn/ui/button';
import { Currency } from '@/constants';
import { useBtcWallet } from '@/networks/btc-like/btc';
import { ResultUtxo } from '@/networks/btc-like/common/transactions/api';
import BtcLikeUtxoSelectModal from '@/shared/components/UtxoSelect/btc-like/BtcLikeSelectModal';

import HeaderWalletDrawerBalanceCardBtcUtxoMergerInfo from './HeaderWalletDrawerBalanceCardBtcUtxoMergerInfo';
import { useBtcUtxoMergeTransaction } from './hooks';

const HeaderWalletDrawerBalanceCardBtcUtxoMerger: FC = () => {
  const { isConnected, address } = useBtcWallet();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUtxos, setSelectedUtxos] = useState<ResultUtxo[]>([]);
  const [vbyteEstimate, setVbyteEstimate] = useState<number>();
  const [shouldShowFiatPrice, setShouldShowFiatPrice] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const { createMergeUtxosTransaction, executeMergeUtxosTransaction } = useBtcUtxoMergeTransaction();

  const handleAcceptSelection = async () => {
    try {
      await createMergeUtxosTransaction(selectedUtxos, vbyteEstimate)
        .then(executeMergeUtxosTransaction)
        .then(() => {
          toast.success('UTXOs consolidation initiated successfully');
        });
    } catch (error) {
      console.error('Failed to merge UTXOs:', error);
    }
  };

  return (
    <>
      <Button
        onClick={() => setIsModalOpen(!isModalOpen)}
        variant="secondary"
        className="font-roboto flex w-full cursor-pointer items-center justify-start gap-2 rounded-xl py-6"
      >
        <div className="flex w-12 justify-center">
          <VscMerge className="size-4" />
        </div>
        <div className="flex w-full flex-row justify-between">Consolidate UTXOs</div>
      </Button>

      {isModalOpen && (
        <BtcLikeUtxoSelectModal
          title="Consolidate your BTC UTXOs"
          description="Select and merge multiple UTXOs into a single one to save on future fees."
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          isShowFiatValue={shouldShowFiatPrice}
          infoComponent={
            <HeaderWalletDrawerBalanceCardBtcUtxoMergerInfo
              shouldShowFiatPrice={shouldShowFiatPrice}
              onToggleFiatPrice={setShouldShowFiatPrice}
              vbyteEstimate={vbyteEstimate}
              onChangeVbyteEstimate={setVbyteEstimate}
              selectedUtxos={selectedUtxos}
              onToggleDisabled={setIsDisabled}
            />
          }
          disabled={!isConnected || isDisabled || !selectedUtxos.length}
          onAcceptSelection={handleAcceptSelection}
          onSelectionChange={setSelectedUtxos}
          currency={Currency.BTC}
          address={address}
        />
      )}
    </>
  );
};

export default HeaderWalletDrawerBalanceCardBtcUtxoMerger;
